/**
 * bbox-thumbnail.js — Shared bbox preview-map helper.
 *
 * Mounts a small OpenLayers map (OSM base + bbox polygon) for a record
 * and, on demand, captures it as a PNG data URL. Used by:
 *
 *   - secims_view/data-catalog/index.html       (metadata modal + PDF print)
 *   - secims_view/map/js/LegendControl.js       (metadata modal + PDF print)
 *   - secims_view/index.html (slice 1+)         (featured-dataset cards)
 *
 * Dual-mode loading:
 *
 *   1. Browser global / <script> tag — the helper reads OpenLayers from
 *      window.ol (CDN UMD build). No configuration needed.
 *
 *   2. Bundled (webpack / ES modules) — the caller passes an `olDeps`
 *      object containing the OL primitives, since window.ol is not
 *      populated in the bundled context.
 *
 *      const olDeps = { Map, View, Feature, Polygon, TileLayer,
 *                       VectorLayer, VectorSource, OSM, Style, Stroke,
 *                       Fill, transformExtent };
 *      const dataUrl = await renderBboxThumbnail({ bbox, olDeps });
 *
 * Public API (exposed on window.SecimsBboxThumbnail and module.exports):
 *
 *   bboxFromGeometry(geom)            -> [w,s,e,n] | null
 *   resolveBbox(data)                 -> [w,s,e,n] | null
 *   initBboxMap(container, data, opt) -> ol.Map | null   (live, modal flow)
 *   captureBboxMap(map)               -> Promise<dataUrl|null>
 *   renderBboxThumbnail(opt)          -> Promise<dataUrl|null> (one-shot, cached)
 */
(function () {
  'use strict';

  // ── Pure helpers ──────────────────────────────────────────────────────────

  function bboxFromGeometry(geom) {
    if (!geom || !geom.type) return null;
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    function visit(c) {
      var x = Number(c[0]), y = Number(c[1]);
      if (!isFinite(x) || !isFinite(y)) return;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    function walk(arr, depth) {
      if (!Array.isArray(arr)) return;
      if (depth === 0) { visit(arr); return; }
      for (var i = 0; i < arr.length; i++) walk(arr[i], depth - 1);
    }
    switch (geom.type) {
      case 'Point':              walk(geom.coordinates, 0); break;
      case 'MultiPoint':         walk(geom.coordinates, 1); break;
      case 'LineString':         walk(geom.coordinates, 1); break;
      case 'MultiLineString':    walk(geom.coordinates, 2); break;
      case 'Polygon':            walk(geom.coordinates, 2); break;
      case 'MultiPolygon':       walk(geom.coordinates, 3); break;
      case 'GeometryCollection':
        (geom.geometries || []).forEach(function (g) {
          var sub = bboxFromGeometry(g);
          if (!sub) return;
          if (sub[0] < minX) minX = sub[0];
          if (sub[1] < minY) minY = sub[1];
          if (sub[2] > maxX) maxX = sub[2];
          if (sub[3] > maxY) maxY = sub[3];
        });
        break;
      default: return null;
    }
    if (!isFinite(minX) || !isFinite(maxX)) return null;
    return [minX, minY, maxX, maxY];
  }

  function resolveBbox(data) {
    if (!data) return null;
    if (Array.isArray(data.bbox) && data.bbox.length >= 4) {
      var nums = data.bbox.slice(0, 4).map(Number);
      if (nums.every(isFinite)) return nums;
    }
    if (data.geometry) return bboxFromGeometry(data.geometry);
    return null;
  }

  // ── OL dependency resolution ──────────────────────────────────────────────

  // Pulls the OL primitives we need either from injected deps or from the
  // CDN UMD shape on window.ol. Returns null if neither is available — the
  // caller treats this as "no map possible" and uses its no-thumbnail fallback.
  function _resolveDeps(injected) {
    if (injected && injected.Map) return injected;
    if (typeof window !== 'undefined' && window.ol) {
      var ol = window.ol;
      return {
        Map: ol.Map,
        View: ol.View,
        Feature: ol.Feature,
        Polygon: ol.geom.Polygon,
        TileLayer: ol.layer.Tile,
        VectorLayer: ol.layer.Vector,
        VectorSource: ol.source.Vector,
        OSM: ol.source.OSM,
        Style: ol.style.Style,
        Stroke: ol.style.Stroke,
        Fill: ol.style.Fill,
        transformExtent: ol.proj.transformExtent
      };
    }
    return null;
  }

  // ── Map construction ──────────────────────────────────────────────────────

  // Build an OL map showing the record's bbox as a polygon over OSM.
  // `target` is an Element (the live <div> the map renders into).
  // Caller owns disposal (setTarget(null)).
  function _buildMap(target, bbox, deps) {
    var extent3857 = deps.transformExtent(bbox, 'EPSG:4326', 'EPSG:3857');
    var ring = [
      [extent3857[0], extent3857[1]],
      [extent3857[2], extent3857[1]],
      [extent3857[2], extent3857[3]],
      [extent3857[0], extent3857[3]],
      [extent3857[0], extent3857[1]]
    ];
    var feature = new deps.Feature(new deps.Polygon([ring]));

    var map = new deps.Map({
      target: target,
      controls: [],            // featured cards are decorative — no zoom UI
      interactions: [],        // and not interactive
      layers: [
        // crossOrigin: 'anonymous' is required so the canvas isn't tainted —
        // captureBboxMap exports the map to a data URL for the PDF, which
        // throws on tainted canvases. OSM's tile server permits CORS.
        new deps.TileLayer({ source: new deps.OSM({ crossOrigin: 'anonymous' }) }),
        new deps.VectorLayer({
          source: new deps.VectorSource({ features: [feature] }),
          style: new deps.Style({
            stroke: new deps.Stroke({ color: '#0057a8', width: 2 }),
            fill: new deps.Fill({ color: 'rgba(0, 87, 168, 0.15)' })
          })
        })
      ],
      view: new deps.View({ projection: 'EPSG:3857' })
    });
    // maxZoom guards against point-like bboxes (W==E or N==S) zooming
    // straight to the OSM tile limit, which renders a useless gray square.
    map.getView().fit(extent3857, { padding: [12, 12, 12, 12], maxZoom: 14 });
    return map;
  }

  // ── Modal-flow entry points (preserve existing call-site behaviour) ──────

  // Mount a preview map inside `container.querySelector('.metadata-bbox-map')`.
  // Returns the ol.Map (so the caller can `setTarget(null)` on modal close)
  // or null if there's no bbox, no target element, or OL isn't available.
  function initBboxMap(container, data, opts) {
    opts = opts || {};
    if (!container) return null;
    var bbox = resolveBbox(data);
    if (!bbox) return null;
    var target = container.querySelector('.metadata-bbox-map');
    if (!target) return null;
    var deps = _resolveDeps(opts.olDeps);
    if (!deps) return null;
    return _buildMap(target, bbox, deps);
  }

  // Wait for rendercomplete, then composite all visible OL layer canvases
  // into a single canvas and return its PNG data URL. Resolves to null on
  // failure (no map, zero size, tainted canvas).
  function captureBboxMap(map) {
    return new Promise(function (resolve) {
      if (!map) return resolve(null);
      map.once('rendercomplete', function () {
        try {
          var size = map.getSize();
          if (!size || !size[0] || !size[1]) return resolve(null);
          var out = document.createElement('canvas');
          out.width = size[0];
          out.height = size[1];
          var ctx = out.getContext('2d');
          var canvases = map.getViewport().querySelectorAll(
            '.ol-layer canvas, canvas.ol-layer'
          );
          canvases.forEach(function (c) {
            if (!c.width) return;
            var opacity = c.parentNode.style.opacity || c.style.opacity;
            ctx.globalAlpha = opacity === '' ? 1 : Number(opacity);
            var transform = c.style.transform;
            var match = transform && transform.match(/^matrix\(([^()]+)\)$/);
            if (match) ctx.setTransform.apply(ctx, match[1].split(',').map(Number));
            else ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.drawImage(c, 0, 0);
          });
          ctx.globalAlpha = 1;
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          resolve(out.toDataURL('image/png'));
        } catch (_) {
          resolve(null);
        }
      });
      map.render();
    });
  }

  // ── One-shot thumbnail (featured cards, future landing-page use) ─────────

  // localStorage cache key. Includes width/height so a card and a print-view
  // never collide; includes `updated` so the cache invalidates on record edit.
  function _cacheKey(bbox, width, height, updated) {
    return 'bbox-thumb:' +
      bbox.map(function (n) { return n.toFixed(5); }).join(',') +
      ':' + width + 'x' + height +
      ':' + (updated || '');
  }

  function _cacheGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_) { return null; }
  }

  function _cacheSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (_) {
      // QuotaExceededError or private-mode storage disabled — fail silently;
      // a thumbnail without caching is still functional, just slower.
    }
  }

  // Render a thumbnail off-screen and return its PNG data URL.
  //
  // opts = {
  //   bbox?: [w,s,e,n],            // preferred; else derived from geometry
  //   geometry?: GeoJSON geometry, // fallback if no bbox
  //   width?: number = 320,
  //   height?: number = 180,
  //   updated?: string,            // record updated/modified timestamp — drives cache invalidation
  //   olDeps?: { ... },            // bundled-context OL primitives
  //   useCache?: boolean = true
  // }
  //
  // Returns Promise<string|null>. null when there's no usable bbox or OL
  // isn't available — the caller renders its no-thumbnail placeholder.
  function renderBboxThumbnail(opts) {
    opts = opts || {};
    var width = opts.width || 320;
    var height = opts.height || 180;
    var bbox = opts.bbox && opts.bbox.length >= 4
      ? opts.bbox.slice(0, 4).map(Number)
      : (opts.geometry ? bboxFromGeometry(opts.geometry) : null);
    if (!bbox || !bbox.every(isFinite)) return Promise.resolve(null);

    var useCache = opts.useCache !== false;
    var key = _cacheKey(bbox, width, height, opts.updated);
    if (useCache) {
      var hit = _cacheGet(key);
      if (hit) return Promise.resolve(hit);
    }

    var deps = _resolveDeps(opts.olDeps);
    if (!deps) return Promise.resolve(null);

    // Position absolutely off-screen with explicit pixel dimensions so OL
    // measures a real viewport and actually draws tiles. visibility:hidden
    // would also work; left:-99999px keeps the element out of any layout
    // calculation the host page might do.
    var holder = document.createElement('div');
    holder.style.cssText =
      'position:fixed;left:-99999px;top:-99999px;' +
      'width:' + width + 'px;height:' + height + 'px;';
    document.body.appendChild(holder);

    var map = _buildMap(holder, bbox, deps);
    return captureBboxMap(map).then(function (dataUrl) {
      try { map.setTarget(null); } catch (_) { /* already disposed */ }
      if (holder.parentNode) holder.parentNode.removeChild(holder);
      if (dataUrl && useCache) _cacheSet(key, dataUrl);
      return dataUrl;
    });
  }

  // ── Export ────────────────────────────────────────────────────────────────

  var api = {
    bboxFromGeometry: bboxFromGeometry,
    resolveBbox: resolveBbox,
    initBboxMap: initBboxMap,
    captureBboxMap: captureBboxMap,
    renderBboxThumbnail: renderBboxThumbnail
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.SecimsBboxThumbnail = api;
  }
})();
